import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * SEO — thin wrapper around react-helmet-async.
 *
 * Usage: <SEO title="Store" description="..." path="/store" />
 *
 * Everything is additive; whatever you don't pass falls back to the
 * site-wide defaults in index.html. Pass `jsonLd` to inject structured
 * data for Rich Results (products, screens, articles).
 */

const SITE_NAME = "Mirrorloops";
const SITE_URL = "https://mirrorloops.com";
const DEFAULT_IMAGE =
  "https://customer-assets.emergentagent.com/job_admin-dashboard-cafe/artifacts/lm6yrhpy_Product%402x.png";

export default function SEO({
  title,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  jsonLd = null,
}) {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} — Advertising for the Physical World`;
  const url = `${SITE_URL}${path || "/"}`;
  const desc =
    description ||
    "Mirrorloops is a programmable physical-media network for DOOH advertising, signage management, and Mac-powered cyber cafes & coworking.";

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd, null, 0)}
        </script>
      )}
    </Helmet>
  );
}

/* ---------- JSON-LD builders ---------- */

export const ORG_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: DEFAULT_IMAGE,
  sameAs: [
    "https://work.mirrorloops.com",
  ],
};

export const SITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/store/search?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export function productLd(p) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    sku: p.id,
    name: p.name,
    brand: { "@type": "Brand", name: p.manufacturer },
    category: p.category,
    description: p.recommendation_basis || p.mirrorloops_use_case || undefined,
    image: p.image_url || undefined,
    url: `${SITE_URL}/store/product/${p.id}`,
    identifier: `mirrorloops://store/sku/${p.id}`,
  };
}

export function screenLd(s) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: s.venue,
    address: { "@type": "PostalAddress", addressLocality: s.market, addressCountry: s.country },
    identifier: s.source_ref,
    url: `${SITE_URL}/s/${s.handle}`,
  };
}
